<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';
import ForwardWhatsappAPI from 'dashboard/api/conversationForwardWhatsapp';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['cancel', 'update:show']);

const { t } = useI18n();
const store = useStore();

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const forwardingInbox = ref({});
const recipientType = ref('contact');
const contactQuery = ref('');
const contacts = ref([]);
const selectedContact = ref(null);
const recipientName = ref('');
const phoneNumber = ref('');
const department = ref('');
const content = ref('');
const isLoadingSummary = ref(false);
const isSearchingContacts = ref(false);
const isSubmitting = ref(false);
const canGenerateSummary = ref(false);
const isConfigurationLoaded = ref(false);

const canForward = computed(() => {
  if (!content.value.trim() || isSubmitting.value) return false;

  if (recipientType.value === 'contact') {
    return !!selectedContact.value?.id;
  }

  return !!phoneNumber.value.trim();
});

const senderLabel = computed(() => {
  const phone = forwardingInbox.value.phone_number;
  return phone
    ? `${forwardingInbox.value.name} - ${phone}`
    : forwardingInbox.value.name;
});

const originLabel = computed(() => {
  const inboxId = props.currentChat?.inbox_id;
  const inbox = inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};

  return (
    props.currentChat?.inbox?.name ||
    inbox?.name ||
    props.currentChat?.inbox_name ||
    t('FORWARD_WHATSAPP.UNKNOWN')
  );
});

const hasOriginName = computed(
  () => originLabel.value !== t('FORWARD_WHATSAPP.UNKNOWN')
);

const onCancel = () => emit('cancel');

const applyDepartmentToContent = message => {
  if (!department.value.trim()) return message;

  return message.replace(
    '*ENCAMINHAMENTO INTERNO*',
    `*ENCAMINHAMENTO INTERNO - ${department.value.trim()}*`
  );
};

const buildFallbackContent = () => {
  const title = department.value
    ? `*ENCAMINHAMENTO INTERNO - ${department.value}*`
    : '*ENCAMINHAMENTO INTERNO*';
  const contact =
    props.currentChat?.meta?.sender?.name || t('FORWARD_WHATSAPP.UNKNOWN');
  const phone =
    props.currentChat?.meta?.sender?.phone_number ||
    t('FORWARD_WHATSAPP.UNKNOWN');
  const origin = originLabel.value;

  return `${title}

*Cliente:* ${contact}
*Telefone:* ${phone}
*Origem:* ${origin}

*Motivo do contato:*
${t('FORWARD_WHATSAPP.UNKNOWN')}

*Informações importantes:*
${t('FORWARD_WHATSAPP.UNKNOWN')}

*Pendências:*
${t('FORWARD_WHATSAPP.UNKNOWN')}

*Próxima ação esperada:*
${t('FORWARD_WHATSAPP.UNKNOWN')}`.trim();
};

const loadConfiguration = async () => {
  try {
    const { data } = await ForwardWhatsappAPI.settings(props.currentChat.id);
    forwardingInbox.value = data.forwarding_inbox || {};
    canGenerateSummary.value = !!data.can_generate_summary;
  } catch (error) {
    useAlert(error.response?.data?.error || t('FORWARD_WHATSAPP.ERROR_CONFIG'));
    onCancel();
  } finally {
    isConfigurationLoaded.value = true;
  }
};

const generateSummary = async () => {
  if (!canGenerateSummary.value) {
    useAlert(t('FORWARD_WHATSAPP.YESZAP_SUMMARY_UNAVAILABLE'));
    return;
  }

  try {
    isLoadingSummary.value = true;
    const { data } = await ForwardWhatsappAPI.summary(props.currentChat.id);
    forwardingInbox.value = data.forwarding_inbox || {};
    content.value = applyDepartmentToContent(
      data.summary || buildFallbackContent()
    );
  } catch (error) {
    useAlert(error.response?.data?.error || t('FORWARD_WHATSAPP.ERROR_CONFIG'));
    onCancel();
  } finally {
    isLoadingSummary.value = false;
  }
};

const searchContacts = async () => {
  if (contactQuery.value.trim().length < 2) {
    contacts.value = [];
    return;
  }

  try {
    isSearchingContacts.value = true;
    const {
      data: { payload = [] },
    } = await ContactAPI.search(contactQuery.value, 1, 'name');
    contacts.value = payload.filter(contact => contact.phone_number);
  } finally {
    isSearchingContacts.value = false;
  }
};

const selectContact = contact => {
  selectedContact.value = contact;
  contactQuery.value = `${contact.name} - ${contact.phone_number}`;
  contacts.value = [];
};

const submit = async () => {
  if (!canForward.value) return;

  try {
    isSubmitting.value = true;
    const payload =
      recipientType.value === 'contact'
        ? {
            recipient_type: 'contact',
            contact_id: selectedContact.value.id,
            department: department.value,
            content: content.value,
          }
        : {
            recipient_type: 'phone',
            recipient_name: recipientName.value,
            phone_number: phoneNumber.value,
            department: department.value,
            content: content.value,
          };

    await ForwardWhatsappAPI.create(props.currentChat.id, payload);
    useAlert(t('FORWARD_WHATSAPP.SUCCESS'));
    onCancel();
  } catch (error) {
    useAlert(error.response?.data?.error || t('FORWARD_WHATSAPP.ERROR_SEND'));
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  if (!hasOriginName.value && props.currentChat?.inbox_id) {
    try {
      await store.dispatch('inboxes/get');
    } catch {
      // The fallback message can still be edited and sent without the inbox list.
    }
  }

  content.value = buildFallbackContent();
  await loadConfiguration();
});
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onCancel">
    <div class="flex max-h-[90vh] flex-col overflow-auto">
      <woot-modal-header
        :header-title="$t('FORWARD_WHATSAPP.TITLE')"
        :header-content="$t('FORWARD_WHATSAPP.DESC')"
      />

      <div class="flex flex-col gap-4 px-6 pb-6">
        <div class="rounded-lg bg-n-alpha-2 p-3">
          <p class="text-xs font-medium text-n-text-muted">
            {{ $t('FORWARD_WHATSAPP.SENDER') }}
          </p>
          <p class="mt-1 text-sm font-medium text-n-text">
            {{ senderLabel || $t('FORWARD_WHATSAPP.LOADING') }}
          </p>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-text">
            {{ $t('FORWARD_WHATSAPP.DEPARTMENT') }}
          </span>
          <input
            v-model="department"
            class="h-10 rounded-lg border-0 bg-n-alpha-2 px-3 text-sm text-n-text outline outline-1 outline-n-weak"
            type="text"
            :placeholder="$t('FORWARD_WHATSAPP.DEPARTMENT_PLACEHOLDER')"
          />
        </label>

        <div class="flex flex-col gap-3">
          <p class="text-sm font-medium text-n-text">
            {{ $t('FORWARD_WHATSAPP.RECIPIENT') }}
          </p>

          <div class="flex flex-wrap gap-4">
            <label class="flex items-center gap-2 text-sm text-n-text">
              <input v-model="recipientType" type="radio" value="contact" />
              {{ $t('FORWARD_WHATSAPP.RECIPIENT_CONTACT') }}
            </label>
            <label class="flex items-center gap-2 text-sm text-n-text">
              <input v-model="recipientType" type="radio" value="phone" />
              {{ $t('FORWARD_WHATSAPP.RECIPIENT_PHONE') }}
            </label>
          </div>

          <div v-if="recipientType === 'contact'" class="relative">
            <input
              v-model="contactQuery"
              class="h-10 w-full rounded-lg border-0 bg-n-alpha-2 px-3 text-sm text-n-text outline outline-1 outline-n-weak"
              type="text"
              :placeholder="$t('FORWARD_WHATSAPP.CONTACT_PLACEHOLDER')"
              @input="searchContacts"
            />
            <div
              v-if="contacts.length"
              class="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg bg-n-solid-2 p-1 outline outline-1 outline-n-weak"
            >
              <button
                v-for="contact in contacts"
                :key="contact.id"
                class="flex w-full flex-col rounded-md px-3 py-2 text-left hover:bg-n-alpha-2"
                type="button"
                @click="selectContact(contact)"
              >
                <span class="text-sm font-medium text-n-text">
                  {{ contact.name }}
                </span>
                <span class="text-xs text-n-text-muted">
                  {{ contact.phone_number }}
                </span>
              </button>
            </div>
            <p
              v-if="isSearchingContacts"
              class="mt-1 text-xs text-n-text-muted"
            >
              {{ $t('FORWARD_WHATSAPP.SEARCHING') }}
            </p>
          </div>

          <div v-else class="grid gap-3 md:grid-cols-2">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-text">
                {{ $t('FORWARD_WHATSAPP.RECIPIENT_NAME') }}
              </span>
              <input
                v-model="recipientName"
                class="h-10 rounded-lg border-0 bg-n-alpha-2 px-3 text-sm text-n-text outline outline-1 outline-n-weak"
                type="text"
                :placeholder="$t('FORWARD_WHATSAPP.RECIPIENT_NAME_PLACEHOLDER')"
              />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-text">
                {{ $t('FORWARD_WHATSAPP.PHONE') }}
              </span>
              <input
                v-model="phoneNumber"
                class="h-10 rounded-lg border-0 bg-n-alpha-2 px-3 text-sm text-n-text outline outline-1 outline-n-weak"
                type="text"
                :placeholder="$t('FORWARD_WHATSAPP.PHONE_PLACEHOLDER')"
              />
            </label>
          </div>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-text">
            {{ $t('FORWARD_WHATSAPP.MESSAGE') }}
          </span>
          <textarea
            v-model="content"
            class="min-h-56 resize-y rounded-lg border-0 bg-n-alpha-2 px-3 py-2 text-sm text-n-text outline outline-1 outline-n-weak"
            :placeholder="$t('FORWARD_WHATSAPP.MESSAGE_PLACEHOLDER')"
          />
        </label>

        <div class="flex justify-end gap-2">
          <NextButton
            faded
            slate
            :label="$t('FORWARD_WHATSAPP.CANCEL')"
            @click.prevent="onCancel"
          />
          <NextButton
            faded
            teal
            icon="i-lucide-sparkles"
            :label="$t('FORWARD_WHATSAPP.GENERATE_SUMMARY')"
            :disabled="!isConfigurationLoaded || !canGenerateSummary"
            :is-loading="isLoadingSummary"
            @click.prevent="generateSummary"
          />
          <NextButton
            icon="i-lucide-send"
            :label="$t('FORWARD_WHATSAPP.SUBMIT')"
            :disabled="!canForward"
            :is-loading="isSubmitting"
            @click.prevent="submit"
          />
        </div>
        <p
          v-if="isConfigurationLoaded && !canGenerateSummary"
          class="text-right text-xs text-n-text-muted"
        >
          {{ $t('FORWARD_WHATSAPP.YESZAP_SUMMARY_UNAVAILABLE') }}
        </p>
      </div>
    </div>
  </woot-modal>
</template>
